import pageModule from "@example/Overrides/Pages/HomePage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setTableColumns from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetTableColumns";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import setVisibleColumnNames from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetVisibleColumnNames";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { CategoryListPageContext } from "@insite/content-library/Pages/CategoryListPage";
import { HomePageContext } from "@insite/content-library/Pages/HomePage";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductGridCard from "@insite/content-library/Widgets/ProductList/ProductListProductGridCard";
import ProductListTable from "@insite/content-library/Widgets/ProductList/ProductListTable";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import sortBy from "lodash/sortBy";
import React, { ReactNode, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImage = "showImage",
    showCompare = "showCompare",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    productListDefaultView = "productListDefaultView",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showCompare]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.productListDefaultView]: ProductListViewType;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: {
                isLoading,
                isSearchPage,
                view,
                productFilters: { pageCategoryId },
            },
        },
    } = state;
    return {
        isLoading,
        productsDataView: getProductListDataView(state),
        view: view || ownProps.fields.productListDefaultView,
        isSearchPage,
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        brandFacets: getProductListDataViewProperty(state, "brandFacets"),
        productLineFacets: getProductListDataViewProperty(state, "productLineFacets"),
        includeRealTimeInventoryAndPricingForSeo:
            getSettingsCollection(state).websiteSettings.includeRealTimeInventoryAndPricingForSeo,
        pageCategoryId,
    };
};

const mapDispatchToProps = {
    setView,
    setTableColumns,
    setVisibleColumnNames,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    noProductsMsg?: TypographyPresentationProps;
    productNotFound?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    searchNotFound?: InjectableCss;
    noResults?: TypographyPresentationProps;
    styleHelp?: TypographyPresentationProps;
    styleSorry?: TypographyPresentationProps;
    styleButton?: InjectableCss;
    redirectButton?: ButtonPresentationProps;
    cardContainerStyles?: CardContainerStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
    homePageButton?: ButtonPresentationProps;
    searchMsg?: InjectableCss;
    helpMsg?: InjectableCss;
    noResultFound?: InjectableCss;
    searchButton?: ButtonPresentationProps;
}

export const listStyles: ProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-top: 20px;
            border-top: 1px solid ${getColor("common.border")};
            padding-bottom: 126px;
        `,
    },
    cardList: {
        gridContainer: {
            gap: 0,
            css: css`
                background: ${getColor("common.accent")};
            `,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
            `,
        },
        gridItem: {
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        },
    },
    searchNotFound: {
        css: css`
            margin: 0 auto;
            width: 70%;
            padding: 0 0.9375rem;
        `,
    },
    noResults: {
        variant: "h3",
        css: css`
            font-size: 32px;
            text-align: center;
            color: ${getColor("primary")};
        `,
    },
    searchMsg: {
        css: css`
            background-color: #eaeaea;
            padding: 36px 0;
            font-weight: 500;
            text-align: center;
        `,
    },
    styleSorry: {
        variant: "p",
        css: css`
            font-size: 20px;
        `,
    },
    styleHelp: {
        variant: "p",
        css: css``,
    },
    styleButton: {
        css: css`
            display: flex;
            justify-content: space-around;
            max-width: 500px;
            margin: 0 auto;
            flex-wrap: wrap;
            margin-top: 20px;
            padding: 0 20px;
        `,
    },
    homePageButton: {
        css: css`
            margin-top: 20px;
            padding-bottom: 40px;
        }
        `,
    },
    redirectButton: {
        css: css`
            margin-bottom: 20px;
            width: 45%;
        `,
    },
    helpMsg: {
        css: css`
            padding-top: 10px;
        `,
    },
    searchButton: {
        css: css`
            width: 160px;
        `,
    },
};

const styles = listStyles;

const ProductListCardList = ({
    isLoading,
    productsDataView,
    view,
    fields,
    attributeTypeFacets,
    brandFacets,
    productLineFacets,
    includeRealTimeInventoryAndPricingForSeo,
    pageCategoryId,
    setView,
    setTableColumns,
    setVisibleColumnNames,
}: Props) => {
    useEffect(() => {
        setView({ view });
    }, []);

    useEffect(() => {
        if (!attributeTypeFacets?.length && !brandFacets?.length && !productLineFacets?.length) {
            setTableColumns({ tableColumns: [] });
            setVisibleColumnNames({ visibleColumnNames: [] });
            return;
        }

        const columns = attributeTypeFacets
            ? sortBy(
                  attributeTypeFacets,
                  o => o.sortOrder,
                  o => o.name,
              )
            : [];
        if (brandFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Brand",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        if (productLineFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Product Line",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        setTableColumns({ tableColumns: columns });
        setVisibleColumnNames({ visibleColumnNames: columns?.slice(0, 3).map(o => o.name) || [] });
    }, [pageCategoryId, attributeTypeFacets?.length, brandFacets?.length, productLineFacets?.length]);

    if (isLoading && !productsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (!productsDataView.value) {
        return null;
    }

    const products = productsDataView.value;
    const totalItemCount = productsDataView.pagination?.totalItemCount;

    if (products.length === 0) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.searchNotFound} data-test-selector="productListNoneFound">
                    <StyledWrapper {...styles.noResultFound}>
                        <Typography {...styles.noResults}>{translate("No Results Found")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.searchMsg}>
                        <StyledWrapper>
                            <Typography {...styles.styleSorry}>
                                {translate("We're sorry, your search returned no results.")}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.helpMsg}>
                            <Typography {...styles.styleHelp}>
                                {translate("May be we can help with these...")}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.styleButton}>
                            <StyledWrapper>
                                <StyledWrapper {...styles.redirectButton}>
                                    <Link href="/Catalog">
                                        <Button {...styles.searchButton}>Categories</Button>
                                    </Link>
                                </StyledWrapper>
                                <StyledWrapper {...styles.redirectButton}>
                                    <Link href="/contactus">
                                        <Button {...styles.searchButton}>Contact Us</Button>
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper>
                                <StyledWrapper {...styles.redirectButton}>
                                    <Link href="/Brands">
                                        <Button {...styles.searchButton}>Brands</Button>
                                    </Link>
                                </StyledWrapper>
                                <StyledWrapper {...styles.redirectButton}>
                                    <Link href="/">
                                        <Button {...styles.searchButton}>Home</Button>
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const renderProducts = (children: ReactNode) => {
        return products.map((product, idx) => (
            <ProductListProductContext product={product} idx={idx} key={product.id}>
                {children}
            </ProductListProductContext>
        ));
    };

    const overriddenProps = { ...fields };
    if (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) {
        overriddenProps.showAvailability = false;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading}>
                <Hidden below="lg">
                    <CardList extendedStyles={styles.cardList} data-test-selector={`productListCards_${view}`}>
                        {view === "Table" ? (
                            <ProductListTable products={products} {...overriddenProps} />
                        ) : (
                            renderProducts(
                                view === "List" ? (
                                    <CardContainer extendedStyles={styles.cardContainerStyles}>
                                        <ProductListProductCard {...overriddenProps} />
                                    </CardContainer>
                                ) : (
                                    <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                                        <ProductListProductGridCard {...overriddenProps} />
                                    </CardContainerMultiColumn>
                                ),
                            )
                        )}
                    </CardList>
                </Hidden>
                <Hidden above="md">
                    <CardList extendedStyles={styles.cardList} data-test-selector="cardListProductsNarrow">
                        {renderProducts(
                            <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                                <ProductListProductGridCard {...overriddenProps} />
                            </CardContainerMultiColumn>,
                        )}
                    </CardList>
                </Hidden>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListCardList),
    definition: {
        group: "Product List",
        displayName: "Card List",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.productListDefaultView,
                editorTemplate: "RadioButtonsField",
                defaultValue: "List",
                fieldType: "General",
                sortOrder: 10,
                options: [
                    {
                        displayName: "List",
                        value: "List",
                    },
                    {
                        displayName: "Grid",
                        value: "Grid",
                    },
                    {
                        displayName: "Table",
                        value: "Table",
                    },
                ],
            },
            {
                name: fields.showImageBadges,
                displayName: "Show Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showTextBadges,
                displayName: "Show Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 14,
            },
        ],
    },
};

export default widgetModule;
